import { LocalDateTime } from '@js-joda/core';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormikActions } from 'components/Form';
import FormSpacing from 'components/Form/FormSpacing';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import useForm from 'hooks/useForm';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import {
  UnitSystem,
  UpdateWearDataArgs,
  UpdateWearDataForm,
  WearDataDto,
  WearDataMutation,
  WearDataForm as ApiWearDataForm
} from 'providers/api';
import React from 'react';
import { UseMutationResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { dayJsToLocalDateTime, LocalDateTimeToDayJs, Weaken } from 'utils';
import UnitSystemNumberField from '../StopePanelPage/RecipesTab/RecipeForm/UnitSystemNumberField';
import wearDataSchema, { WearDataInputs } from './WearDataSchema';

type CreateMut = UseMutationResult<string, unknown, WearDataMutation, unknown>;
type UpdateMut = UseMutationResult<void, unknown, UpdateWearDataArgs, unknown>;

interface WearDataFormProps {
  wearData?: WearDataDto;
  projectId: string;
  inspectionPointId: string;
  unitSystemPreference: UnitSystem;
  successPath: string;
  mutation:
  | UseMutationResult<string, unknown, WearDataMutation, unknown>
  | UseMutationResult<void, unknown, UpdateWearDataArgs, unknown>;
  onChange: (updatedData: Weaken<WearDataInputs>) => void;
}

const toNum = (v: unknown): number | undefined => (v === '' || v === null || v === undefined ? undefined : Number(v));

const WearDataForm = ({
  wearData,
  projectId,
  inspectionPointId,
  unitSystemPreference,
  successPath,
  mutation,
  onChange,
}: WearDataFormProps) => {
  const navigate = useNavigate();
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);

  const { formik, helpers } = useForm<WearDataInputs>({
    mutation,
    formikConfig: {
      initialValues: {
        inspectionPointId,
        inspectionDate: wearData?.inspectionDate.toLocalDateTime() ?? LocalDateTime.now(),
        position1: wearData?.position1,
        position2: wearData?.position2,
        position3: wearData?.position3,
        position4: wearData?.position4,
        position5: wearData?.position5,
        position6: wearData?.position6,
        position7: wearData?.position7,
        position8: wearData?.position8,
      },
      validationSchema: wearDataSchema,
      onSubmit: async (wearDataForm, { setSubmitting }) => {
        setSubmitting(true);
        try {
          const inspectionDate = wearDataForm.inspectionDate ?? LocalDateTime.now();

          if (wearData) {
            const updateForm: UpdateWearDataForm = {
              inspectionDate,
              position1: toNum(wearDataForm.position1),
              position2: toNum(wearDataForm.position2),
              position3: toNum(wearDataForm.position3),
              position4: toNum(wearDataForm.position4),
              position5: toNum(wearDataForm.position5),
              position6: toNum(wearDataForm.position6),
              position7: toNum(wearDataForm.position7),
              position8: toNum(wearDataForm.position8),
            };

            const payload: UpdateWearDataArgs = {
              wearDataId: wearData.entityId,
              form: updateForm,
            };

            await (mutation as UpdateMut).mutateAsync(payload);
          } else {
            const createForm: ApiWearDataForm = {
              inspectionPointId,
              inspectionDate,
              position1: toNum(wearDataForm.position1),
              position2: toNum(wearDataForm.position2),
              position3: toNum(wearDataForm.position3),
              position4: toNum(wearDataForm.position4),
              position5: toNum(wearDataForm.position5),
              position6: toNum(wearDataForm.position6),
              position7: toNum(wearDataForm.position7),
              position8: toNum(wearDataForm.position8),
            };

            const payload: WearDataMutation = {
              mineModelId: projectId,
              form: createForm,
            };

            await (mutation as CreateMut).mutateAsync(payload);
          }

          navigate(successPath);
        } finally {
          setSubmitting(false);
        }
      },
    },
  });

  const {
    values,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setTouched,
    setErrors,
    validateForm,
  } = formik;

  const handleInspectionDateChange = async (value: Dayjs | null) => {
    if (value === null) {
      setFieldValue('inspectionDate', value);
      return;
    }
    const processedDateTime = dayJsToLocalDateTime(value);
    setFieldValue('inspectionDate', processedDateTime);
    setTouched({ ...touched, inspectionDate: true } as any);
    const e = await validateForm({ ...values, inspectionDate: processedDateTime });
    setErrors(e);
  };

  React.useEffect(() => {
    onChange(values);
  }, [values]);

  return (
    <Container maxWidth="lg">
      <form onSubmit={handleSubmit}>
        <Box component={Paper} sx={{ p: 2, mb: 2, mt: 2 }}>
          <FormSpacing>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                open={datePickerOpen}
                onOpen={() => setDatePickerOpen(true)}
                onClose={() => setDatePickerOpen(false)}
                format="DD/MM/YYYY"
                name="inspectionDate"
                label={<Trans id="Inspection Date">Inspection Date</Trans>}
                value={values.inspectionDate ? LocalDateTimeToDayJs(values.inspectionDate) : null}
                onChange={handleInspectionDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    helperText: helpers.getErrorHelpText('inspectionDate'),
                    error: helpers.hasError('inspectionDate'),
                    autoComplete: 'off',
                    placeholder: '__/__/____',
                    onClick: () => {
                      setDatePickerOpen(true);
                      setFieldValue('inspectionDate', null);
                    },
                    onBlur: handleBlur,
                  },
                }}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>

            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <UnitSystemNumberField
                key={item}
                size="small"
                unitSystem={unitSystemPreference}
                customUnitFields={[]}
                unitInfo={unitLabels[BasicDisplayUnits.SmallDistance]}
                decimalPlaces={2}
                type="number"
                error={helpers.hasError(`position${item}`)}
                helperText={helpers.getErrorHelpText(`position${item}`)}
                required
                fullWidth
                id={`position${item}`}
                name={`position${item}`}
                label={`${i18n._('Position')} ${item}`}
                value={values[`position${item}` as keyof WearDataInputs]}
                onChange={(e) => setFieldValue(`position${item}`, toNum(e.target.value))}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 0.01 }}
              />
            ))}
          </FormSpacing>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormikActions
            formik={formik}
            mutation={mutation}
            submitText={i18n._('Save')}
            right={['reset', 'submit']}
          />
        </Box>
      </form>
    </Container>
  );
};

export default WearDataForm;
